// app/src/screens/CreateHome.jsx — the everyday entry (handoff §1 brand-home + §2 first-run),
// pixel-close. CREATE-2: everything is REAL now. Brands come from GET /v1/brands (the context
// bar + mobile sheet pickers); the resume strip reads GET /v1/drafts?active=1 and renders each
// draft's `resume` pointer VERBATIM (never re-derived); Recent reads GET /v1/runs; first-run is
// a REAL signal (zero runs + zero drafts + zero brands). Submit = createDraft + draftIdeate →
// Ideas. No fixtures, no fake attachment chips (uploads are an honest coming-soon). Every
// string via t().

import React, { useEffect, useRef, useState } from 'react';
import { Paperclip, Play, ChevronDown, Check, Plus } from 'lucide-react';
import { navigate } from '@common/router.js';
import { useDismiss } from '@common/dismiss.js';
import { listBrands, listDrafts, createDraft, draftIdeate } from '@common/drafts.js';
import { newIdemKey } from '@common/commands.js';
import { useI18n } from '../i18n/index.jsx';
import { useFlow } from '../lib/flow.jsx';
import { useSession } from '../lib/session.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';
import { useToast } from '../lib/toast.jsx';
import { Sheet } from '../components/Sheet.jsx';
import { AccountMenu } from '../components/AccountFoot.jsx';
import { resumeRoute, isFirstRun } from '../lib/create-nav.js';
import { brandLetter, brandGradient } from '../lib/brand-visual.js';

// resume.to → the strip's plain-language "where you left off" label
const RESUME_LABEL = {
  prompt: 'create.draftPrompt', ideating: 'create.draftIdeas', ideas: 'create.draftIdeas',
  scripting: 'create.draftScript', script: 'create.draftScript',
  generating: 'create.draftGenerating', result: 'create.draftResult',
};
const RECENT_GRADIENTS = ['var(--poster-coral)', 'var(--poster-violet)', 'var(--poster-mint-flat)'];

function firstName(me, fallback) {
  const n = me && me.display_name;
  return n ? n.split(' ')[0] : fallback;
}

// the Single/Series segmented toggle (Series is an honest coming-soon — CREATE-1 §11)
function ModeToggle({ mode, onSeries, t }) {
  return (
    <div className="seg">
      <button className={'opt' + (mode === 'single' ? ' on' : '')} onClick={() => {}}>{t('create.single')}</button>
      <button className={'opt' + (mode === 'series' ? ' on' : '')} onClick={onSeries}>{t('create.series')}</button>
    </div>
  );
}

// a picker that is a bottom Sheet on mobile and an anchored popover on desktop (the Result
// version-history pattern). Content is shared between both surfaces.
function Picker({ open, onClose, mobile, title, children }) {
  const ref = useRef(null);
  useDismiss(ref, onClose, { enabled: open && !mobile });
  if (!open) return null;
  if (mobile) return <Sheet open={open} onClose={onClose} title={title}>{children}</Sheet>;
  return (
    <div ref={ref} style={{ position: 'absolute', top: 'calc(100% + 6px)', insetInlineEnd: 0, zIndex: 40, minWidth: 232, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: 'var(--shadow-pop)', padding: 8 }}>
      {children}
    </div>
  );
}

export default function CreateHome() {
  const { t, locale } = useI18n();
  const { setDraftId } = useFlow();
  const { api, me } = useSession();
  const mobile = useIsMobile();
  const toast = useToast();

  const [prompt, setPrompt] = useState('');
  const [brands, setBrands] = useState(null);
  const [drafts, setDrafts] = useState(null);
  const [runs, setRuns] = useState(null);
  const [brandId, setBrandId] = useState(null);
  const [productName, setProductName] = useState(null);
  const [brandPick, setBrandPick] = useState(false);
  const [productPick, setProductPick] = useState(false);
  const [acctSheet, setAcctSheet] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    Promise.all([listBrands(api), listDrafts(api, { limit: 6 }), api.get('runs')]).then(([b, d, r]) => {
      if (!alive) return;
      const bl = b.kind === 'ok' ? (b.body.brands || []) : [];
      setBrands(bl);
      setDrafts(d.kind === 'ok' ? (d.body.drafts || []) : []);
      setRuns(r.kind === 'ok' ? (r.body.runs || []) : []);
      if (bl.length && brandId == null) { setBrandId(bl[0].id); setProductName((bl[0].products && bl[0].products[0] && bl[0].products[0].name) || null); }
    });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loaded = brands != null && drafts != null && runs != null;
  const brand = (brands || []).find((b) => b.id === brandId) || null;
  const products = (brand && brand.products) || [];

  const pickBrand = (id) => {
    setBrandId(id);
    const b = (brands || []).find((x) => x.id === id) || null;
    setProductName((b && b.products && b.products[0] && b.products[0].name) || null);
    setBrandPick(false);
  };

  async function goIdeas() {
    const p = prompt.trim();
    if (!p) { toast(t('create.promptEmpty')); return; }
    if (busy) return;
    setBusy(true);
    // SETTINGS-THREAD: seed ONLY the UI locale (the language default). We do NOT seed aspect —
    // seeding it as a chip would beat the prompt-parse ("make a 16:9 ad" would be ignored) and
    // suppress the "auto" label; the engine defaults+labels aspect itself (9:16). (review S2)
    const cd = await createDraft(api, { prompt: p, settings: { locale: (typeof locale === 'function' ? locale() : locale) || 'en-US' }, brandId: brandId || null });
    if (cd.kind !== 'ok') { setBusy(false); toast(cd.error || t('common.error')); return; }
    setDraftId(cd.body.id);
    // kick the first idea set (free in-allowance enqueues a job Ideas watches; a 6th-set gate
    // mutates nothing — Ideas re-quotes the price). ONE idempotency key per this press.
    await draftIdeate(api, cd.body.id, { idempotencyKey: newIdemKey() });
    setBusy(false);
    navigate('/create/ideas');
  }

  const onSeries = () => toast(t('toast.seriesSoon'));

  const addFileChip = (
    <button className="softchip" onClick={() => toast(t('toast.addFile'))}>
      <Paperclip size={14} strokeWidth={2.1} />{t('create.addFile')}
    </button>
  );
  const getIdeasBtn = (
    <button className="q-btn press btn-primary" disabled={busy} onClick={goIdeas} style={busy ? { opacity: 0.6 } : undefined}>
      {t('create.getIdeas')} <span style={{ fontSize: 16 }}>→</span>
    </button>
  );
  const firstRunIdeasBtn = (
    <button className="q-btn press" disabled={busy} onClick={goIdeas} style={{ display: 'inline-flex', alignItems: 'center', gap: 9, background: 'var(--coral)', color: '#fff', padding: '15px 30px', borderRadius: 999, font: '700 16px var(--font-body)', boxShadow: '0 8px 20px rgba(255,81,71,.3)', opacity: busy ? 0.6 : 1 }}>
      {t('create.getIdeas')} <span style={{ fontSize: 17 }}>→</span>
    </button>
  );

  if (!loaded) return <div className="view"><p className="muted">{t('common.loading')}</p></div>;

  const firstRun = isFirstRun({ runs, drafts, brands });

  // ---------- FIRST-RUN (a genuinely empty account) ----------
  if (firstRun) {
    const spine = [
      { n: 1, c: 'var(--coral)', k: 'firstRun.s1' }, { n: 2, c: 'var(--tangerine)', k: 'firstRun.s2' },
      { n: 3, c: 'var(--mint)', k: 'firstRun.s3' }, { n: 4, c: 'var(--violet)', k: 'firstRun.s4' },
    ];
    return (
      <div className="view" style={{ maxWidth: 720, margin: '36px auto 0', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: 'var(--coral-tint)', borderRadius: 999, padding: '6px 13px', marginBottom: 20 }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--coral)', animation: 'pulseDot 1.6s ease-in-out infinite' }} />
          <span className="mono" style={{ font: '700 10.5px var(--font-mono)', color: 'var(--coral)', letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('firstRun.welcome')}</span>
        </div>
        <h1 className="h-first" style={{ marginBottom: 12 }}>{t('firstRun.titlePrefix')} <span className="accent">{t('firstRun.titleAccent')}</span></h1>
        <p className="muted" style={{ fontSize: 16, lineHeight: 1.5, margin: '0 0 26px' }}>{t('firstRun.subtitle')}</p>
        <div style={{ display: 'inline-flex', marginBottom: 16 }}><ModeToggle mode="single" onSeries={onSeries} t={t} /></div>
        <div className="prompt-card" style={{ textAlign: 'start' }}>
          <textarea className="prompt-input" value={prompt} placeholder={t('create.promptPlaceholder')} onChange={(e) => setPrompt(e.target.value)} />
          <div style={{ flex: 1 }} />
          <div className="prompt-actions">
            {addFileChip}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--text-faint)', font: '600 12.5px var(--font-body)', background: 'var(--fill-chip)', padding: '8px 14px', borderRadius: 999 }}>{t('firstRun.addBrand')} <span className="mono" style={{ font: '400 11px var(--font-mono)', color: 'var(--text-faintest)' }}>{t('firstRun.optional')}</span></span>
          </div>
        </div>
        <div style={{ marginTop: 18 }}>{firstRunIdeasBtn}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 11, marginTop: 26, flexWrap: 'wrap', font: '600 12px var(--font-body)', color: 'var(--text-faint)' }}>
          {spine.map((s, i) => (
            <React.Fragment key={s.n}>
              {i > 0 && <span style={{ color: 'var(--text-divider)' }}>→</span>}
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 19, height: 19, borderRadius: 999, background: s.c, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10 }}>{s.n}</span>{t(s.k)}
              </span>
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  }

  // ---------- BRAND-HOME ----------
  const name = firstName(me, t('create.you'));
  // the newest draft that actually has somewhere to resume TO (a prompt-stage draft — incl. one
  // whose only idea set failed — resumes to '/', i.e. right here, so it gets no dead "resume" card).
  const resumeDraft = drafts.find((d) => d.resume && d.resume.to && d.resume.to !== 'prompt') || null;

  const brandRow = (b, active, onClick) => (
    <button key={b ? b.id : 'none'} className="q-btn" onClick={onClick} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 12, background: active ? 'var(--fill-ghost)' : 'var(--surface)', border: active ? 'none' : '1px solid var(--border)', borderRadius: 13, padding: 11, textAlign: 'start', marginBottom: 6 }}>
      <span style={{ width: 34, height: 34, borderRadius: 10, background: b ? brandGradient(b) : 'var(--fill-track)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 14px var(--font-body)', color: '#fff', flex: 'none' }}>{b ? brandLetter(b) : '—'}</span>
      <span style={{ flex: 1 }}><span style={{ display: 'block', font: '700 13.5px var(--font-body)' }}>{b ? b.name : t('create.noBrand')}</span>{b && b.one_liner && <span style={{ display: 'block', font: '400 11px var(--font-mono)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.one_liner}</span>}</span>
      {active && <Check size={16} color="var(--mint)" strokeWidth={2.4} />}
    </button>
  );
  const brandList = (
    <>
      {brandRow(null, brandId == null, () => pickBrand(null))}
      {brands.map((b) => brandRow(b, b.id === brandId, () => pickBrand(b.id)))}
      <button className="q-btn" onClick={() => { setBrandPick(false); navigate('/brands'); }} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, background: 'transparent', color: 'var(--text-muted)', padding: 12, font: '700 12.5px var(--font-body)', border: 'none', marginTop: 2 }}><Plus size={14} strokeWidth={2.2} />{t('create.newBrand')}</button>
    </>
  );
  const productList = (
    <>
      <button className="q-btn" onClick={() => { setProductName(null); setProductPick(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: productName == null ? 'var(--fill-ghost)' : 'var(--surface)', border: productName == null ? 'none' : '1px solid var(--border)', borderRadius: 13, padding: 11, textAlign: 'start', marginBottom: 6 }}>
        <span style={{ flex: 1, font: '700 13.5px var(--font-body)' }}>{t('create.noProduct')}</span>{productName == null && <Check size={16} color="var(--mint)" strokeWidth={2.4} />}
      </button>
      {products.map((p) => (
        <button key={p.name} className="q-btn" onClick={() => { setProductName(p.name); setProductPick(false); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, background: p.name === productName ? 'var(--fill-ghost)' : 'var(--surface)', border: p.name === productName ? 'none' : '1px solid var(--border)', borderRadius: 13, padding: 11, textAlign: 'start', marginBottom: 6 }}>
          <span style={{ flex: 1 }}><span style={{ display: 'block', font: '700 13.5px var(--font-body)' }}>{p.name}</span>{p.desc && <span style={{ display: 'block', font: '400 11px var(--font-mono)', color: 'var(--text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.desc}</span>}</span>
          {p.name === productName && <Check size={16} color="var(--mint)" strokeWidth={2.4} />}
        </button>
      ))}
      {products.length === 0 && <p className="muted" style={{ fontSize: 12, padding: '6px 8px', margin: 0 }}>{brand ? t('create.noProductsYet') : t('create.pickBrandFirst')}</p>}
    </>
  );

  const brandPicker = (
    <span style={{ position: 'relative', flex: mobile ? 1 : 'none' }}>
      <button className="q-btn" onClick={() => setBrandPick((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: mobile ? '9px 12px' : '7px 13px 7px 8px', borderRadius: mobile ? 13 : 999, background: 'var(--surface)', border: '1px solid var(--border)', width: mobile ? '100%' : 'auto' }}>
        <span style={{ width: 21, height: 21, borderRadius: 999, background: brand ? brandGradient(brand) : 'var(--fill-track)', display: 'flex', alignItems: 'center', justifyContent: 'center', font: '700 10px var(--font-body)', color: '#fff' }}>{brand ? brandLetter(brand) : '+'}</span>
        <span style={{ font: '700 13px var(--font-body)' }}>{brand ? brand.name : t('create.noBrand')}</span>
        <ChevronDown size={13} color="var(--text-faint)" style={{ marginInlineStart: mobile ? 'auto' : 0 }} />
      </button>
      <Picker open={brandPick} onClose={() => setBrandPick(false)} mobile={mobile} title={t('create.chooseBrand')}>{brandList}</Picker>
    </span>
  );
  const productPicker = (
    <span style={{ position: 'relative', flex: mobile ? 1 : 'none' }}>
      <button className="q-btn" onClick={() => setProductPick((v) => !v)} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: mobile ? '9px 12px' : '7px 13px', borderRadius: mobile ? 13 : 999, background: 'var(--surface)', border: '1px solid var(--border)', width: mobile ? '100%' : 'auto', justifyContent: 'space-between' }}>
        <span style={{ font: '700 13px var(--font-body)', color: productName ? 'var(--ink)' : 'var(--text-faint)' }}>{productName || t('create.noProduct')}</span>
        <ChevronDown size={13} color="var(--text-faint)" />
      </button>
      <Picker open={productPick} onClose={() => setProductPick(false)} mobile={mobile} title={t('create.chooseProduct')}>{productList}</Picker>
    </span>
  );

  return (
    <div className="view">
      {mobile && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 2px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src="/logo-mark.png" alt="" style={{ width: 22, height: 22 }} /><span style={{ font: '700 18px var(--font-body)', letterSpacing: '-.02em' }}>{t('app.short')}</span></div>
          <button className="q-btn" onClick={() => setAcctSheet(true)} style={{ width: 32, height: 32, borderRadius: 999, background: 'var(--avatar-grad)', color: '#fff', font: '700 13px var(--font-body)', border: 'none' }}>{(me && (me.display_name || me.email) || 'D').charAt(0).toUpperCase()}</button>
        </div>
      )}

      {resumeDraft && (
        <button className="q-btn press" onClick={() => { setDraftId(resumeDraft.id); navigate(resumeRoute(resumeDraft.resume)); }} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: mobile ? 11 : 13, background: 'var(--violet-tint)', border: '1px solid var(--violet-line)', borderRadius: mobile ? 15 : 16, padding: mobile ? '11px 13px' : '13px 16px', marginBottom: mobile ? 14 : 18, textAlign: 'start' }}>
          <span style={{ width: mobile ? 34 : 38, height: mobile ? 34 : 38, borderRadius: mobile ? 10 : 11, background: 'var(--violet)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}><Play size={mobile ? 15 : 18} color="#fff" fill="#fff" strokeWidth={0} /></span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', font: `700 ${mobile ? 12.5 : 13.5}px var(--font-body)` }}>{t('create.resume')}</span>
            <span style={{ display: 'block', font: `400 ${mobile ? 11 : 12}px var(--font-body)`, color: 'var(--violet-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{(resumeDraft.prompt ? resumeDraft.prompt.slice(0, 40) : t('create.untitledDraft'))} · {t(RESUME_LABEL[resumeDraft.resume && resumeDraft.resume.to] || 'create.draftIdeas')}</span>
          </span>
          <span style={{ font: `700 ${mobile ? 12 : 12.5}px var(--font-body)`, color: 'var(--violet)', flex: 'none' }}>{mobile ? '→' : `${t('create.continue')} →`}</span>
        </button>
      )}

      {/* context bar */}
      {mobile ? (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>{brandPicker}{productPicker}</div>
          <div style={{ marginBottom: 16 }}><ModeToggle mode="single" onSeries={onSeries} t={t} /></div>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <ModeToggle mode="single" onSeries={onSeries} t={t} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="eyebrow" style={{ letterSpacing: '.08em' }}>{t('create.for')}</span>
            {brandPicker}{productPicker}
          </div>
        </div>
      )}

      <h1 className="h-hero" style={{ marginBottom: mobile ? 14 : 18 }}>{t('create.greetingPrefix')} <span className="accent">{name}?</span></h1>

      <div className="prompt-card">
        <textarea className="prompt-input" value={prompt} placeholder={t('create.promptPlaceholder')} onChange={(e) => setPrompt(e.target.value)} />
        <div style={{ flex: 1 }} />
        <div className="prompt-actions">
          {addFileChip}
          {!mobile && getIdeasBtn}
        </div>
      </div>

      {mobile && <div style={{ marginTop: 16 }}>{getIdeasBtn}</div>}

      {runs.length > 0 && !mobile && (
        <>
          <div style={{ marginTop: 30 }}><span className="eyebrow">{t('create.recent')}</span></div>
          <div style={{ display: 'flex', gap: 14, marginTop: 12, flexWrap: 'wrap' }}>
            {runs.slice(0, 3).map((r, i) => (
              <button key={r.run_id} className="q-btn qcard lift" onClick={() => navigate(`/result/${encodeURIComponent(r.run_id)}`)} style={{ width: 150, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden', textAlign: 'start', padding: 0 }}>
                <div style={{ height: 88, background: RECENT_GRADIENTS[i % RECENT_GRADIENTS.length] }} />
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ font: '700 12.5px var(--font-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title || r.run_id}</div>
                  <div style={{ font: '400 10.5px var(--font-mono)', color: 'var(--text-faint)' }}>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : (r.mode || t('content.run'))}</div>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* mobile account sheet */}
      <Sheet open={acctSheet} onClose={() => setAcctSheet(false)} title={me ? (me.display_name || me.email) : t('app.short')}>
        <AccountMenu onClose={() => setAcctSheet(false)} />
      </Sheet>
    </div>
  );
}
